import { useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Form, Input, Picker, SpinLoading, Toast } from 'antd-mobile';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { registerMemberFromWhatsApp } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useWsQuickRegisterGate } from '../hooks/useWsQuickRegisterGate';
import { markRegistrationRefreshPending } from '../utils/registrationSession';
import { clearWsToken, resolveWsTokenForWsRoute, buildLoginFromWsPath, isWsPhoneRequiredError, isWsTokenError } from '../utils/wsToken';
import './RegisterPage.css';

const genderOptions = [
    { label: 'Male', value: 1 },
    { label: 'Female', value: 0 },
    { label: 'Other', value: 2 },
];

export default function RegisterFromWsPage() {
    const { loginFromWsApiResult } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [genderPickerVisible, setGenderPickerVisible] = useState(false);
    const [datePickerVisible, setDatePickerVisible] = useState(false);
    const [name, setName] = useState('');
    const [gender, setGender] = useState([]);
    const [dateOfBirth, setDateOfBirth] = useState(null);
    const [registering, setRegistering] = useState(false);
    const submitGuardRef = useRef(false);

    const wsToken = resolveWsTokenForWsRoute(searchParams);
    const { gateReady } = useWsQuickRegisterGate(wsToken);

    useEffect(() => {
        if (!wsToken && gateReady) {
            Toast.show({ icon: 'fail', content: 'Missing registration link. Please open this page from WhatsApp.' });
        }
    }, [wsToken, gateReady]);

    const handleSubmit = async () => {
        if (!wsToken) {
            Toast.show({ content: 'Missing registration link. Please open this page from WhatsApp.' });
            return;
        }
        if (submitGuardRef.current) {
            return;
        }
        if (!name || name.trim().length === 0) {
            Toast.show({ content: 'Please enter your name' });
            return;
        }
        if (gender.length === 0) {
            Toast.show({ content: 'Please select your gender' });
            return;
        }
        if (!dateOfBirth) {
            Toast.show({ content: 'Please select your date of birth' });
            return;
        }

        const formattedDOB = `${dateOfBirth.getFullYear()}-${String(dateOfBirth.getMonth() + 1).padStart(2, '0')}-${String(dateOfBirth.getDate()).padStart(2, '0')}`;

        setRegistering(true);
        submitGuardRef.current = true;
        try {
            const result = await registerMemberFromWhatsApp(wsToken, name.trim(), gender[0], formattedDOB);
            const profile = await loginFromWsApiResult(result, { nickname: name.trim() });

            if (profile?.id == null || profile?.id === '') {
                Toast.show({ icon: 'fail', content: 'Registration succeeded but session is incomplete. Please refresh the page.' });
                return;
            }

            clearWsToken();
            markRegistrationRefreshPending();
            Toast.show({ icon: 'success', content: 'Registration successful!' });
            navigate('/', { replace: true });
        } catch (error) {
            submitGuardRef.current = false;
            if (isWsPhoneRequiredError(error?.message, error?.code)) {
                clearWsToken();
                navigate('/register', { replace: true });
                return;
            }
            if (isWsTokenError(error?.message)) {
                Toast.show({
                    icon: 'fail',
                    content:
                        'Registration link is not ready or has expired. Please tap the button in WhatsApp again, or register with SMS code below.',
                });
                clearWsToken();
                navigate('/register', { replace: true });
                return;
            }
            Toast.show({ icon: 'fail', content: error.message || 'Registration failed, please try again later' });
        } finally {
            setRegistering(false);
        }
    };

    if (wsToken && !gateReady) {
        return (
            <div className="loading-screen">
                <SpinLoading color="primary" />
                <span>Preparing registration…</span>
            </div>
        );
    }

    return (
        <div className="register-page">
            <div className="register-card">
                <div className="register-header">
                    <h1 className="register-title">TaoGo Member Registration</h1>
                    <h2 className="register-subtitle">WhatsApp</h2>
                    <p className="register-description">Please fill in your name, gender and date of birth</p>
                </div>

                <div className="register-form">
                    <div className="form-section-title">Your Details</div>

                    <Form layout="vertical" footer={null}>
                        <Form.Item label={<span>Your Name<span className="required-mark">*</span></span>}>
                            <Input
                                value={name}
                                onChange={setName}
                                placeholder="Enter your name"
                                clearable
                                disabled={!wsToken}
                            />
                        </Form.Item>

                        <Form.Item label={<span>Gender<span className="required-mark">*</span></span>}>
                            <div
                                onClick={() => wsToken && setGenderPickerVisible(true)}
                                className="select-field"
                                style={!wsToken ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
                            >
                                {gender.length > 0
                                    ? genderOptions.find((opt) => opt.value === gender[0])?.label
                                    : 'Select an option'}
                            </div>
                            <Picker
                                columns={[genderOptions]}
                                visible={genderPickerVisible}
                                onClose={() => setGenderPickerVisible(false)}
                                value={gender}
                                onConfirm={(v) => {
                                    setGender(v);
                                    setGenderPickerVisible(false);
                                }}
                            />
                        </Form.Item>

                        <Form.Item label={<span>Date of Birth<span className="required-mark">*</span></span>}>
                            <div
                                onClick={() => wsToken && setDatePickerVisible(true)}
                                className="select-field"
                                style={!wsToken ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
                            >
                                {dateOfBirth
                                    ? dateOfBirth.toLocaleDateString('en-GB')
                                    : 'Select date'}
                            </div>
                            <DatePicker
                                visible={datePickerVisible}
                                onClose={() => setDatePickerVisible(false)}
                                value={dateOfBirth}
                                onConfirm={(v) => {
                                    setDateOfBirth(v);
                                    setDatePickerVisible(false);
                                }}
                                max={new Date()}
                                min={new Date(1926, 0, 1)}
                            />
                        </Form.Item>
                    </Form>

                    <Button
                        color="primary"
                        onClick={handleSubmit}
                        loading={registering}
                        block
                        className="register-button"
                        disabled={!wsToken}
                    >
                        Register
                    </Button>

                    <div className="login-link">
                        Already have an account?{' '}
                        <a href={buildLoginFromWsPath(wsToken)}>Login</a>
                    </div>
                </div>
            </div>
        </div>
    );
}
